import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Lock, Mail, Loader2, LogIn } from "lucide-react";
import { motion } from "framer-motion";
import LoginLoader from "../../Component/Loader/LoginLoader";
import { AuthContext } from "../../context/AuthContext";

export default function CompanyLogin() {
  const [contact_email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { setAuthUser } = useContext(AuthContext);

  const brandBlue = "#0B5ED7";
  const brandDarkBlue = "#0A3E91";

  useEffect(() => {
    const user = localStorage.getItem("authUserData");
    const role = localStorage.getItem("userRole");

    if (user && role) redirectBasedOnRole(role);
  }, []);

  const getRoleRoute = (role) => {
    const map = {
      1: "company",
      2: "agent",
      3: "employee",
      4: "retailer",
    };
    return map[role] || "company";
  };

  const redirectBasedOnRole = (role) => {
    switch (role) {
      case "company":
        navigate("/dashboard");
        break;
      case "agent":
        navigate("/agentdashboard");
        break;
      case "employee":
        navigate("/empdashboard");
        break;
      case "retailer":
        navigate("/retailerdashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  const validate = () => {
    let emailErr = "";
    let pwdErr = "";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!contact_email) emailErr = "Enter Email";
    else if (!emailRe.test(contact_email)) emailErr = "Enter valid email";

    if (!password) pwdErr = "Enter password";
    else if (password.length < 6) pwdErr = "Min 6 characters";

    setErrors({ email: emailErr, pwd: pwdErr });
    return !emailErr && !pwdErr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        "${BASE_URL}/common/login",
        { email: contact_email, password }
      );

      if (response.data.status && response.data.data) {
        const userData = response.data.data;
        const detectedRole = getRoleRoute(userData.role);

        localStorage.setItem("authUserData", JSON.stringify(userData));
        localStorage.setItem("userRole", detectedRole);

        setAuthUser(userData);

        // 🔥 Correct logic order
        if (userData.is_verified === null) {
          navigate("/onboard");
        } else if (userData.is_verified === 5) {
          redirectBasedOnRole(detectedRole);
        } else {
          alert("Your account is pending approval.");
        }
      } else {
        alert("Invalid credentials");
      }
    } catch (_) {
      alert("Something went wrong");
    }

    setLoading(false);
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <LoginLoader />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className={`w-full max-w-md mx-auto ${loading ? "opacity-40 pointer-events-none select-none" : ""}`}
      >
        <div className="mb-4 text-center">
          <h1 className="mt-2 text-xl md:text-2xl font-semibold" style={{ color: brandDarkBlue }}>
            User Login
          </h1>
        </div>

        <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-6 md:p-7">
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            <label className="block text-xs md:text-sm font-medium text-slate-600">Email</label>
            <div className={`relative rounded-xl border ${errors.email ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
              <span className="absolute left-3 top-2.5 text-slate-400"><Mail size={16} /></span>
              <input
                value={contact_email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.com"
                className="w-full rounded-xl pl-9 pr-3 py-2"
              />
            </div>
            {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}

            <label className="block text-xs md:text-sm font-medium text-slate-600">Password</label>
            <div className={`relative rounded-xl border ${errors.pwd ? "border-red-400" : "border-slate-200"} focus-within:border-slate-400`}>
              <span className="absolute left-3 top-2.5 text-slate-400"><Lock size={16} /></span>
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="•••••••"
                className="w-full rounded-xl pl-9 pr-9 py-2"
              />
              <button type="button" className="absolute right-2 top-2 p-1" onClick={() => setShowPwd((s) => !s)}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.pwd && <p className="text-xs text-red-500">{errors.pwd}</p>}

            <button className="mt-1 w-full rounded-xl px-3 py-2 font-semibold text-white shadow-sm"
              style={{ backgroundColor: brandBlue }} disabled={loading}>
              {loading ? <><Loader2 className="animate-spin" size={16} /> Signing in…</> : <><LogIn size={16} /> Sign in</>}
            </button>
          </form>
        </div>
      </motion.div>
    </>
  );
}





// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, EyeOff, Lock, Mail, Loader2, LogIn } from "lucide-react";
// import { motion } from "framer-motion";
// import LoginLoader from "../../Component/Loader/LoginLoader";
// import { AuthContext } from "../../context/AuthContext";

// export default function CompanyLogin() {
//   const [contact_email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [flag, setFlag] = useState("company");

//   const navigate = useNavigate();
//   const { setAuthUser } = useContext(AuthContext);

//   const brandBlue = "#0B5ED7";
//   const brandDarkBlue = "#0A3E91";

//   useEffect(() => {
//     const user = localStorage.getItem("authUserData");
//     const role = localStorage.getItem("userRole");

//     if (user) redirectBasedOnRole(role);
//   }, []);

//   const redirectBasedOnRole = (role) => {
//     switch (role) {
//       case "company":
//         navigate("/dashboard");
//         break;
//       case "agent":
//         navigate("/agentdashboard");
//         break;
//       case "employee":
//         navigate("/empdashboard");
//         break;
//       case "retailer":
//         navigate("/retailerdashboard");
//         break;
//       default:
//         navigate("/dashboard");
//     }
//   };

//   const validate = () => {
//     let emailErr = "";
//     let pwdErr = "";
//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

//     if (!contact_email) emailErr = "Enter Email";
//     else if (!emailRe.test(contact_email)) emailErr = "Enter valid email";

//     if (!password) pwdErr = "Enter password";
//     else if (password.length < 6) pwdErr = "Min 6 characters";

//     setErrors({ email: emailErr, pwd: pwdErr });
//     return !emailErr && !pwdErr;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);
//     try {
//       const response = await axios.post(
//         "${BASE_URL}/common/login",
//         {
//           email: contact_email,
//           password,
//           flag,
//         }
//       );

//       if (response.data.status && response.data.data) {
//         const userData = response.data.data;

//         localStorage.setItem("authUserData", JSON.stringify(userData));
//         localStorage.setItem("userRole", flag);

//         setAuthUser(userData);

//         if (userData.is_verified === null) navigate("/onboard");
//         else if (userData.is_verified === 5) redirectBasedOnRole(flag);
//         else alert("Your account is not verified.");
//       } else {
//         alert("Invalid credentials");
//       }
//     } catch (_) {
//       alert("Something went wrong");
//     }

//     setLoading(false);
//   };

//   return (
//     <>
//       {loading && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
//           <LoginLoader />
//         </div>
//       )}

//       <motion.div
//         initial={{ opacity: 0, y: 16 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.35 }}
//         className={`w-full max-w-md mx-auto ${loading ? "opacity-40 pointer-events-none select-none" : ""}`}
//       >
//         <div className="mb-4 text-center">
//           <h1
//             className="mt-2 text-xl md:text-2xl font-semibold"
//             style={{ color: brandDarkBlue }}
//           >
//             User Login
//           </h1>
//         </div>

//         <div className="rounded-2xl border bg-white shadow-sm p-4 sm:p-6 md:p-7">
//           <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
//             {/* Email */}
//             <label className="block text-xs md:text-sm font-medium text-slate-600">
//               Email
//             </label>

//             <div
//               className={`relative rounded-xl border ${
//                 errors.email ? "border-red-400" : "border-slate-200"
//               } focus-within:border-slate-400`}
//             >
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 <Mail size={16} />
//               </span>

//               <input
//                 value={contact_email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 placeholder="example@mail.com"
//                 className="w-full rounded-xl pl-9 pr-3 py-2 text-sm md:text-base outline-none"
//               />
//             </div>
//             {errors.email && (
//               <p className="text-xs text-red-500">{errors.email}</p>
//             )}

//             {/* Password */}
//             <label className="block text-xs md:text-sm font-medium text-slate-600">
//               Password
//             </label>

//             <div
//               className={`relative rounded-xl border ${
//                 errors.pwd ? "border-red-400" : "border-slate-200"
//               } focus-within:border-slate-400`}
//             >
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 <Lock size={16} />
//               </span>

//               <input
//                 type={showPwd ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 placeholder="•••••••"
//                 className="w-full rounded-xl pl-9 pr-9 py-2 text-sm md:text-base outline-none"
//               />

//               <button
//                 type="button"
//                 className="absolute right-2 top-2 p-1 rounded-lg text-slate-500 hover:bg-slate-100"
//                 onClick={() => setShowPwd((s) => !s)}
//               >
//                 {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//               </button>
//             </div>
//             {errors.pwd && <p className="text-xs text-red-500">{errors.pwd}</p>}

//             {/* Role Select */}
//             <label className="block text-xs md:text-sm font-medium text-slate-600">
//               Login As
//             </label>
//             <select
//               className="border w-full p-2 rounded-xl outline-none"
//               value={flag}
//               onChange={(e) => setFlag(e.target.value)}
//             >
//               <option value="company">Company</option>
//               <option value="agent">Agent</option>
//               <option value="employee">Employee</option>
//               <option value="retailer">Retailer</option>
//             </select>

//             <button
//               type="submit"
//               disabled={loading}
//               className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm md:text-base font-semibold text-white shadow-sm disabled:opacity-70"
//               style={{ backgroundColor: brandBlue }}
//             >
//               {loading ? (
//                 <>
//                   <Loader2 className="animate-spin" size={16} /> Signing in…
//                 </>
//               ) : (
//                 <>
//                   <LogIn size={16} /> Sign in
//                 </>
//               )}
//             </button>
//           </form>
//         </div>
//       </motion.div>
//     </>
//   );
// }
 


// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, EyeOff, Lock, Mail, Loader2, LogIn } from "lucide-react";
// import { motion } from "framer-motion";
// import LoginLoader from "../../Component/Loader/LoginLoader";
// import { AuthContext } from "../../context/AuthContext";

// export default function CompanyLogin() {
//   const [contact_email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [flag, setFlag] = useState("company");

//   const navigate = useNavigate();
//   const { setAuthUser } = useContext(AuthContext);

//   // Redirect if already logged in
//   useEffect(() => {
//     const user = localStorage.getItem("authUserData");
//     const role = localStorage.getItem("userRole");

//     if (user) {
//       redirectBasedOnRole(role);
//     }
//   }, []);

//   // Role-based dashboard redirection
//   const redirectBasedOnRole = (role) => {
//     switch (role) {
//       case "company":
//         navigate("/dashboard");
//         break;
//       case "agent":
//         navigate("/agentdashboard");
//         break;
//       case "employee":
//         navigate("/empdashboard");
//         break;
//       case "retailer":
//         navigate("/retailerdashboard");
//         break;
//       default:
//         navigate("/dashboard");
//     }
//   };

//   // Validation
//   const validate = () => {
//     let emailErr = "";
//     let pwdErr = "";

//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

//     if (!contact_email) emailErr = "Enter Email";
//     else if (!emailRe.test(contact_email)) emailErr = "Enter valid email";

//     if (!password) pwdErr = "Enter password";
//     else if (password.length < 6) pwdErr = "Min 6 characters";

//     setErrors({ email: emailErr, pwd: pwdErr });
//     return !emailErr && !pwdErr;
//   };

//   // Submit Login
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);

//     try {
//       const response = await axios.post(
//         "${BASE_URL}/common/login",
//         {
//           email: contact_email,
//           password,
//           flag,
//         }
//       );

//       if (response.data.status && response.data.data) {
//         const userData = response.data.data;

//         localStorage.setItem("authUserData", JSON.stringify(userData));
//         localStorage.setItem("userRole", flag);

//         setAuthUser(userData);

//         // NEW LOGIC ------------------------------
//         if (userData.is_verified === null) {
//           navigate("/onboard");
//         } else if (userData.is_verified === 5) {
//           redirectBasedOnRole(flag);
//         } else {
//           alert("Your account is not verified.");
//         }
//         // ----------------------------------------
//       } else {
//         alert("Invalid credentials");
//       }
//     } catch (error) {
//       alert("Something went wrong");
//     }

//     setLoading(false);
//   };

//   return (
//     <>
//       {loading && <LoginLoader />}

//       <motion.div className="max-w-md w-full">
//         <h1 className="text-center text-xl font-bold">User Login</h1>

//         <form onSubmit={handleSubmit} className="space-y-4 mt-4">
//           {/* Email */}
//           <label>Email</label>
//           <input
//             className="border w-full p-2"
//             value={contact_email}
//             onChange={(e) => setEmail(e.target.value)}
//           />
//           {errors.email && (
//             <p className="text-red-500 text-xs">{errors.email}</p>
//           )}

//           {/* Password */}
//           <label>Password</label>
//           <div className="relative">
//             <input
//               className="border w-full p-2"
//               type={showPwd ? "text" : "password"}
//               value={password}
//               onChange={(e) => setPassword(e.target.value)}
//             />
//             <span
//               onClick={() => setShowPwd(!showPwd)}
//               className="absolute right-3 top-3 cursor-pointer"
//             >
//               {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
//             </span>
//           </div>
//           {errors.pwd && (
//             <p className="text-red-500 text-xs">{errors.pwd}</p>
//           )}

//           {/* Login As */}
//           <label>Login As</label>
//           <select
//             className="border w-full p-2"
//             value={flag}
//             onChange={(e) => setFlag(e.target.value)}
//           >
//             <option value="company">Company</option>
//             <option value="agent">Agent</option>
//             <option value="employee">Employee</option>
//             <option value="retailer">Retailer</option>
//           </select>

//           {/* Submit */}
//           <button className="bg-blue-600 text-white w-full py-2 rounded-md flex justify-center gap-2">
//             {loading ? (
//               <Loader2 size={18} className="animate-spin" />
//             ) : (
//               <LogIn size={18} />
//             )}
//             Sign In
//           </button>
//         </form>
//       </motion.div>
//     </>
//   );
// }



// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, EyeOff, Lock, Mail, Loader2, LogIn } from "lucide-react";
// import { motion } from "framer-motion";
// import LoginLoader from "../../Component/Loader/LoginLoader";
// import { AuthContext } from "../../context/AuthContext";

// export default function CompanyLogin() {

//   const [contact_email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [flag, setFlag] = useState("company");

//   const navigate = useNavigate();
//   const { setAuthUser } = useContext(AuthContext);

//   useEffect(() => {
//     const user = localStorage.getItem("authUserData");
//     const onboard = localStorage.getItem("onboard_completed");
//     const role = localStorage.getItem("userRole");

//     if (user) {
//       if (!onboard) navigate("/onboard");
//       else redirectBasedOnRole(role);
//     }
//   }, []);

//   const redirectBasedOnRole = (role) => {
//     switch (role) {
//       case "company": navigate("/dashboard"); break;
//       case "agent": navigate("/agentdashboard"); break;
//       case "employee": navigate("/empdashboard"); break;
//       case "retailer": navigate("/retailerdashboard"); break;
//       default: navigate("/dashboard");
//     }
//   };

//   const validate = () => {
//     let emailErr = "";
//     let pwdErr = "";
//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

//     if (!contact_email) emailErr = "Enter Email";
//     else if (!emailRe.test(contact_email)) emailErr = "Enter valid email";

//     if (!password) pwdErr = "Enter password";
//     else if (password.length < 6) pwdErr = "Min 6 characters";

//     setErrors({ email: emailErr, pwd: pwdErr });
//     return !emailErr && !pwdErr;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setLoading(true);
//     try {
//       const response = await axios.post("${BASE_URL}/common/login", {
//         email: contact_email,
//         password,
//         flag
//       });

//       if (response.data.status && response.data.data) {
//         localStorage.setItem("authUserData", JSON.stringify(response.data.data));
//         localStorage.setItem("userRole", flag);
//         setAuthUser(response.data.data);

//         const onboarded = localStorage.getItem("onboard_completed");

//         if (!onboarded) navigate("/onboard");
//         else redirectBasedOnRole(flag);
//       } else {
//         alert("Invalid credentials");
//       }
//     } catch (error) {
//       alert("Something went wrong");
//     }
//     setLoading(false);
//   };

//   return (
//     <>
//       {loading && <LoginLoader />}
//       <motion.div className="max-w-md w-full">
//         <h1 className="text-center text-xl font-bold">User Login</h1>

//         <form onSubmit={handleSubmit} className="space-y-4 mt-4">
//           <label>Email</label>
//           <input className="border w-full p-2" value={contact_email} onChange={(e) => setEmail(e.target.value)} />
//           {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}

//           <label>Password</label>
//           <div className="relative">
//             <input className="border w-full p-2" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
//             <span onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-3 cursor-pointer">
//               {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
//             </span>
//           </div>
//           {errors.pwd && <p className="text-red-500 text-xs">{errors.pwd}</p>}

//           <label>Login As</label>
//           <select className="border w-full p-2" value={flag} onChange={(e) => setFlag(e.target.value)}>
//             <option value="company">Company</option>
//             <option value="agent">Agent</option>
//             <option value="employee">Employee</option>
//             <option value="retailer">Retailer</option>
//           </select>

//           <button className="bg-blue-600 text-white w-full py-2 rounded-md flex justify-center gap-2">
//             {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />} Sign In
//           </button>
//         </form>
//       </motion.div>
//     </>
//   );
// }




// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, EyeOff, Lock, Mail, Loader2, LogIn } from "lucide-react";
// import { motion } from "framer-motion";
// import LogoImg from "../../asset/images/goelectronixB.png";
// import { AuthContext } from "../../context/AuthContext";
// import LoginLoader from "../../Component/Loader/LoginLoader";


// export default function CompanyLogin() {
//   const [contact_email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPwd, setShowPwd] = useState(false);
//   const [errors, setErrors] = useState({ email: "", pwd: "" });
//   const [loading, setLoading] = useState(false);
//   const [flag, setFlag] = useState("company");

//   const navigate = useNavigate();
//   const { setAuthUser } = useContext(AuthContext);

//   useEffect(() => {
//     const stored = localStorage.getItem("authUserData");
//     if (stored) navigate("/company-dashboard", { replace: true });
//   }, [navigate]);

//   // VALIDATION
//   const validate = () => {
//     let ok = true;
//     let emailErr = "";
//     let pwdErr = "";

//     const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

//     if (!contact_email.trim()) {
//       emailErr = "Enter email";
//       ok = false;
//     } else if (!emailRe.test(contact_email)) {
//       emailErr = "Enter valid email";
//       ok = false;
//     }

//     if (!password) {
//       pwdErr = "Enter password";
//       ok = false;
//     } else if (password.length < 6) {
//       pwdErr = "Min 6 characters";
//       ok = false;
//     }

//     setErrors({ contact_email: emailErr, pwd: pwdErr });
//     return ok;
//   };

//   // SUBMIT LOGIN
//   // SUBMIT LOGIN
// const handleSubmit = async (e) => {
//   e.preventDefault();
//   if (!validate()) return;

//   setLoading(true);

//   try {
//     const payload = {
//       flag: flag, // role selected
//       email: contact_email,
//       password: password,
//     };

//     const response = await axios.post(
//       "${BASE_URL}/common/login",
//       payload
//     );

//     if (response.data.status === true && response.data.data) {
//       const userData = response.data.data;

//       localStorage.setItem("authUserData", JSON.stringify(userData));
//       setAuthUser(userData);

//       // REDIRECT BASED ON ROLE
//       if (["company", "agent", "retailer"].includes(flag)) {
//         navigate("/onboard");
//       } else if (flag === "employee") {
//         navigate("/empdashboard");
//       } else {
//         // fallback
//         navigate("/dashboard");
//       }
//     } else {
//       alert(response.data.message || "Invalid credentials. Try again.");
//     }
//   } catch (err) {
//     console.error(err);
//     alert("Login failed. Please try again.");
//   } finally {
//     setLoading(false);
//   }
// };


//   const brandBlue = "#0B5ED7";
//   const brandDarkBlue = "#0A3E91";

//   return (
//     // <div className="relative min-h-screen bg-slate-50 flex items-center justify-center px-3 sm:px-6">

//       <>
//       {loading && (
//         <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
//           <LoginLoader />
//         </div>
//       )}

//       <motion.div
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 0.35 }}
//         className={`w-full max-w-md ${loading ? "opacity-40 pointer-events-none" : ""}`}
//       >

//         {/* HEADER */}
//         <div className="mb-4 text-center">
//           {/* <img src={LogoImg} alt="Logo" className="mx-auto h-10" /> */}
//           <h1
//             className="mt-2 text-xl md:text-2xl font-semibold"
//             style={{ color: brandDarkBlue }}
//           >
//             User Login
//           </h1>
//         </div>

//         {/* LOGIN CARD */}
//         <div className="border bg-white rounded-2xl shadow-sm p-6 md:p-7">
//           <form onSubmit={handleSubmit} className="space-y-4">

//             {/* Email */}
//             <label className="text-sm font-medium text-slate-600">
//               Email Address
//             </label>
//             <div
//               className={`relative rounded-xl border ${
//                 errors.email ? "border-red-400" : "border-slate-200"
//               } focus-within:border-slate-400`}
//             >
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 <Mail size={16} />
//               </span>
//               <input
//                 value={contact_email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 placeholder="company@example.com"
//                 className="w-full pl-9 pr-3 py-2 rounded-xl outline-none text-sm"
//               />
//             </div>
//             {errors.contact_email && (
//               <p className="text-xs text-red-500">{errors.contact_email}</p>
//             )}

//             {/* Password */}
//             <label className="text-sm font-medium text-slate-600">
//               Password
//             </label>
//             <div
//               className={`relative rounded-xl border ${
//                 errors.pwd ? "border-red-400" : "border-slate-200"
//               } focus-within:border-slate-400`}
//             >
//               <span className="absolute left-3 top-2.5 text-slate-400">
//                 <Lock size={16} />
//               </span>

//               <input
//                 type={showPwd ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 placeholder="••••••••"
//                 className="w-full pl-9 pr-9 py-2 rounded-xl outline-none text-sm"
//               />

//               <button
//                 type="button"
//                 onClick={() => setShowPwd(!showPwd)}
//                 className="absolute right-2 top-2 p-1 rounded-md text-slate-500 hover:bg-slate-100"
//               >
//                 {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
//               </button>
//             </div>

//             <div>
//                 {/* Login Role */}
//                 <label className="text-sm font-medium text-slate-600">Login As</label>
//                 <select
//                 value={flag}
//                 onChange={(e) => setFlag(e.target.value)}
//                 className="w-full border border-slate-300 rounded-xl px-3 py-2 mb-2 text-sm"
//                 >
//                 <option value="company">Company</option>
//                 <option value="agent">Agent</option>
//                 <option value="employee">Employee</option>
//                 <option value="retailer">Retailer</option>
//                 </select>

//             </div>

//             {errors.pwd && <p className="text-xs text-red-500">{errors.pwd}</p>}

//             {/* Submit */}
//             <button
//               type="submit"
//               className="w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-semibold text-white shadow-sm"
//               style={{ backgroundColor: brandBlue }}
//             >
//               {loading ? (
//                 <>
//                   <Loader2 className="animate-spin" size={16} /> Signing in…
//                 </>
//               ) : (
//                 <>
//                   <LogIn size={16} /> Sign in
//                 </>
//               )}
//             </button>
//           </form>
//         </div>

//         {/* <p className="mt-4 text-center text-[11px] md:text-xs text-slate-500">
//           © {new Date().getFullYear()} RR Tech
//         </p> */}
//       </motion.div>
//       </>
//     // {/* </div> */}
//   );
// }
